export type FieldValue = string | boolean | null | undefined;

export type Validator = (value: FieldValue) => string | null;

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const PHONE_REGEX = /^(?:(?:\+|00)33|0)\s*[1-9](?:[\s.-]*\d{2}){4}$/;
const NAME_REGEX = /^[a-zA-ZÀ-ÖØ-öø-ÿ' -]+$/;

const asString = (value: FieldValue): string =>
	typeof value === "string" ? value : "";

/** Champ obligatoire (texte non vide ou case cochée) */
export const required =
	(message = "Ce champ est requis."): Validator =>
	(value) => {
		if (typeof value === "boolean") return value ? null : message;
		return asString(value).trim() ? null : message;
	};

/** Format e-mail (champ vide autorisé : à combiner avec `required` si obligatoire) */
export const email =
	(message = "Veuillez saisir une adresse e-mail valide."): Validator =>
	(value) => {
		const v = asString(value).trim();
		if (!v) return null;
		return EMAIL_REGEX.test(v) ? null : message;
	};

/** Format téléphone français (champ vide autorisé) */
export const phone =
	(message = "Veuillez saisir un numéro de téléphone valide."): Validator =>
	(value) => {
		const v = asString(value).trim();
		if (!v) return null;
		return PHONE_REGEX.test(v) ? null : message;
	};

/** Nom / prénom : au moins 2 caractères, lettres uniquement (champ vide autorisé) */
export const personName =
	(
		message = "Veuillez saisir un nom valide (au moins 2 lettres, sans chiffres).",
	): Validator =>
	(value) => {
		const v = asString(value).trim();
		if (!v) return null;
		return v.length >= 2 && NAME_REGEX.test(v) ? null : message;
	};

/** Longueur minimale (champ vide autorisé) */
export const minLength =
	(min: number, message?: string): Validator =>
	(value) => {
		const v = asString(value).trim();
		if (!v) return null;
		return v.length >= min
			? null
			: (message ??
					`Ce champ doit contenir au moins ${min} caractères.`);
	};

/** Case à cocher obligatoire */
export const checked =
	(message = "Vous devez cocher cette case."): Validator =>
	(value) =>
		value === true ? null : message;

export function runValidators(
	value: FieldValue,
	validators: Validator[] = [],
): string | null {
	for (const validator of validators) {
		const error = validator(value);
		if (error) return error;
	}
	return null;
}
